import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MyAssignmentsPanel } from '../components/MyAssignmentsPanel';
import { ActivityFeed } from '../components/ActivityFeed';
import { AdoptionWidget } from '../components/AdoptionWidget';
import { AskStewieAdoptionWidget } from '../components/AskStewieAdoptionWidget';
import { fetchAppLinksConfig, navigateToApp, DEFAULT_RETURN_PATHS } from '../utils/appLinks';
import {
  Robot,
  ClipboardText,
  Command,
  BookOpen,
  Barcode,
  Clock,
  ArrowRight,
  Users,
  Briefcase,
  Warning,
  Lightning,
  ArrowSquareOut,
  Queue,
  CalendarBlank,
  ChatCircle,
  Lightbulb,
  Timer,
  Export,
  Flag,
} from '@phosphor-icons/react';

// Hub App Colors mapping
const APP_COLORS = {
  blue: 'from-blue-500 to-blue-600',
  emerald: 'from-emerald-500 to-emerald-600',
  amber: 'from-amber-500 to-amber-600',
  violet: 'from-violet-500 to-violet-600',
  slate: 'from-slate-500 to-slate-600',
  orange: 'from-orange-500 to-orange-600',
};

const APP_BG_COLORS = {
  blue: 'bg-blue-50 border-blue-200',
  emerald: 'bg-emerald-50 border-emerald-200',
  amber: 'bg-amber-50 border-amber-200',
  violet: 'bg-violet-50 border-violet-200',
  slate: 'bg-slate-50 border-slate-200',
  orange: 'bg-orange-50 border-orange-200',
};

export const HubDashboard = () => {
  const { user, api, isSupervisorPlus, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingAssignmentsCount, setPendingAssignmentsCount] = useState(0);
  const [appLinksConfig, setAppLinksConfig] = useState({ links: {}, navConfig: {} });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch app links config
        const config = await fetchAppLinksConfig(api);
        setAppLinksConfig(config);

        // Fetch Hub Snapshot
        const snapshotRes = await api.get('/hub/snapshot').catch(() => ({ data: null }));
        if (snapshotRes.data) {
          setSnapshot(snapshotRes.data);
        }

        // Fetch pending assignments count
        const assignmentsRes = await api.get('/my/assignments').catch(() => ({ data: [] }));
        const pending = (assignmentsRes.data || []).filter(a => a.acknowledgement?.status === 'pending');
        setPendingAssignmentsCount(pending.length);
      } catch (error) {
        console.error('Failed to fetch hub data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [api]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  /**
   * Navigate to external app site (full redirect) or internal route for Timesheets
   */
  const handleAppNavigation = (appKey, returnToPath = null) => {
    // Timesheets is ALWAYS internal - never external redirect
    if (appKey === 'timesheets') {
      navigate('/timesheets');
      return;
    }
    
    const result = navigateToApp(appKey, returnToPath, appLinksConfig);
    
    if (result.blocked && result.reason === 'coming_soon') {
      // Don't navigate - app is coming soon
      return;
    }
    
    if (result.useRouter) {
      // Fallback to internal route (config not available)
      navigate(result.url);
    }
    // Otherwise, navigateToApp already handled the redirect via window.location.href
  };

  /**
   * Quick action buttons - also redirect to external sites (except timesheets)
   */
  const handleQuickAction = (appKey, path) => {
    handleAppNavigation(appKey, path);
  };

  const widgets = snapshot?.widgets || {};

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="hub-dashboard">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-5 mb-2">
          <img 
            src="/getgridgate-logo.png" 
            alt="GetGridGate" 
            className="w-32 h-32 object-contain"
          />
          <div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">
              {getGreeting()}, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-slate-600">
              Welcome to GETGRIDGATE Hub — your unified operations center
            </p>
          </div>
        </div>
      </div>

      {/* Pending Assignments Alert */}
      {pendingAssignmentsCount > 0 && (
        <div className="mb-6">
          <MyAssignmentsPanel />
        </div>
      )}

      {/* Snapshot Widgets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
        
        {/* Quick Canvass Widget */}
        <Card className={`border-2 ${APP_BG_COLORS.emerald} hover:shadow-lg transition-all cursor-pointer group`}
              onClick={() => handleAppNavigation('quickcanvass', '/staffing-requests')}
              data-testid="widget-quickcanvass">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${APP_COLORS.emerald} rounded-lg flex items-center justify-center`}>
                  <ClipboardText size={22} className="text-white" weight="fill" />
                </div>
                <CardTitle className="text-lg">Quick Canvass</CardTitle>
              </div>
              <ArrowSquareOut size={20} className="text-slate-400 group-hover:text-emerald-600 transition-colors" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Queue size={16} className="text-blue-500" />
                  <span className="text-xs text-slate-500">Queued</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.quickcanvass?.queued_staffing_requests || 0}
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Users size={16} className="text-amber-500" />
                  <span className="text-xs text-slate-500">Unfilled</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.quickcanvass?.unfilled_slots || 0}
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Warning size={16} className="text-red-500" />
                  <span className="text-xs text-slate-500">Forced Fill</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.quickcanvass?.forced_fills_pending || 0}
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarBlank size={16} className="text-emerald-500" />
                  <span className="text-xs text-slate-500">Today</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.quickcanvass?.canvasses_started_today || 0}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full group-hover:bg-emerald-600 group-hover:text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); handleAppNavigation('quickcanvass', '/staffing-requests'); }}
            >
              Open Staffing Requests
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Project Command Center Widget */}
        <Card className={`border-2 ${APP_BG_COLORS.blue} hover:shadow-lg transition-all cursor-pointer group`}
              onClick={() => handleAppNavigation('pcc', '/workorders')}
              data-testid="widget-pcc">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${APP_COLORS.blue} rounded-lg flex items-center justify-center`}>
                  <Command size={22} className="text-white" weight="fill" />
                </div>
                <CardTitle className="text-lg">Command Center</CardTitle>
              </div>
              <ArrowSquareOut size={20} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase size={16} className="text-blue-500" />
                  <span className="text-xs text-slate-500">Active WOs</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.pcc?.active_work_orders || 0}
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Warning size={16} className="text-amber-500" />
                  <span className="text-xs text-slate-500">Overdue</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.pcc?.overdue_updates_today || 0}
                </p>
              </div>
              {isSupervisorPlus() && (
                <div className="col-span-2 p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-2 mb-1">
                    <Timer size={16} className="text-red-500" />
                    <span className="text-xs text-slate-500">Expiring Soon (3 days)</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {loading ? '-' : widgets.pcc?.expiring_soon || 0}
                  </p>
                </div>
              )}
            </div>
            <Button 
              variant="outline" 
              className="w-full group-hover:bg-blue-600 group-hover:text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); handleAppNavigation('pcc', '/workorders'); }}
            >
              View Work Orders
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Station Log Book Widget */}
        <Card className={`border-2 ${APP_BG_COLORS.amber} hover:shadow-lg transition-all cursor-pointer group`}
              onClick={() => handleAppNavigation('stationlogbook', '/')}
              data-testid="widget-stationlogbook">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${APP_COLORS.amber} rounded-lg flex items-center justify-center`}>
                  <BookOpen size={22} className="text-white" weight="fill" />
                </div>
                <CardTitle className="text-lg">Station Log Book</CardTitle>
              </div>
              <ArrowSquareOut size={20} className="text-slate-400 group-hover:text-amber-600 transition-colors" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarBlank size={16} className="text-amber-500" />
                  <span className="text-xs text-slate-500">Today</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.stationlogbook?.logs_today || 0}
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Warning size={16} className="text-slate-500" />
                  <span className="text-xs text-slate-500">Not Linked</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.stationlogbook?.logs_not_linked || 0}
                </p>
              </div>
              {isSupervisorPlus() && (
                <div className="col-span-2 p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-2 mb-1">
                    <Flag size={16} className="text-red-500" />
                    <span className="text-xs text-slate-500">Flagged (needs review)</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {loading ? '-' : widgets.stationlogbook?.flagged_logs || 0}
                  </p>
                </div>
              )}
            </div>
            <Button 
              variant="outline" 
              className="w-full group-hover:bg-amber-600 group-hover:text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); handleAppNavigation('stationlogbook', '/'); }}
            >
              View Today's Logs
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* AskStewie Widget */}
        <Card className={`border-2 ${APP_BG_COLORS.violet} hover:shadow-lg transition-all cursor-pointer group`}
              onClick={() => handleAppNavigation('askstewie', '/')}
              data-testid="widget-askstewie">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${APP_COLORS.violet} rounded-lg flex items-center justify-center`}>
                  <Robot size={22} className="text-white" weight="fill" />
                </div>
                <CardTitle className="text-lg">AskStewie</CardTitle>
              </div>
              <ArrowSquareOut size={20} className="text-slate-400 group-hover:text-violet-600 transition-colors" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <ChatCircle size={16} className="text-violet-500" />
                  <span className="text-xs text-slate-500">Today</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.askstewie?.conversations_today || 0}
                </p>
              </div>
              {isSupervisorPlus() && (
                <div className="p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-2 mb-1">
                    <Lightbulb size={16} className="text-amber-500" />
                    <span className="text-xs text-slate-500">Pending</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {loading ? '-' : widgets.askstewie?.pending_learnings || 0}
                  </p>
                </div>
              )}
            </div>
            <Button 
              variant="outline" 
              className="w-full group-hover:bg-violet-600 group-hover:text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); handleAppNavigation('askstewie', '/'); }}
            >
              Ask a Question
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Timesheets Widget (INTERNAL MODULE) */}
        <Card className={`border-2 ${APP_BG_COLORS.orange} hover:shadow-lg transition-all cursor-pointer group`}
              onClick={() => handleAppNavigation('timesheets', '/')}
              data-testid="widget-timesheets">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${APP_COLORS.orange} rounded-lg flex items-center justify-center`}>
                  <Clock size={22} className="text-white" weight="fill" />
                </div>
                <CardTitle className="text-lg">Timesheets</CardTitle>
              </div>
              <ArrowRight size={20} className="text-slate-400 group-hover:text-orange-600 transition-colors" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Timer size={16} className="text-orange-500" />
                  <span className="text-xs text-slate-500">Drafts</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.timesheets?.drafts || 0}
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Export size={16} className="text-emerald-500" />
                  <span className="text-xs text-slate-500">Exported</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {loading ? '-' : widgets.timesheets?.exported || 0}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full group-hover:bg-orange-600 group-hover:text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); handleAppNavigation('timesheets', '/'); }}
            >
              View Timesheets
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* RFID Tool Tracker Widget (Coming Soon) */}
        <Card className={`border-2 ${APP_BG_COLORS.slate} opacity-75`} data-testid="widget-rfid">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${APP_COLORS.slate} rounded-lg flex items-center justify-center`}>
                  <Barcode size={22} className="text-white" weight="fill" />
                </div>
                <CardTitle className="text-lg">RFID Tool Tracker</CardTitle>
              </div>
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300">Coming Soon</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-white/50 rounded-lg border text-center mb-4">
              <Barcode size={32} className="text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Track equipment and tools with RFID technology</p>
            </div>
            <Button variant="outline" className="w-full" disabled>
              Coming Soon
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Adoption Tracking (Supervisor+) */}
      {isSupervisorPlus() && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <AdoptionWidget department="substation" />
          <AskStewieAdoptionWidget department="substation" />
        </div>
      )}

      {/* Quick Actions */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightning size={20} weight="fill" className="text-primary" />
            Quick Actions
          </CardTitle>
          <CardDescription>Jump to common tasks across your apps</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => handleQuickAction('pcc', '/workorders/new')} className="gap-2">
              <Command size={18} />
              Create Work Order
            </Button>
            <Button variant="outline" onClick={() => handleQuickAction('quickcanvass', '/staffing-requests')} className="gap-2">
              <Queue size={18} />
              View Staffing Queue
            </Button>
            <Button variant="outline" onClick={() => handleQuickAction('stationlogbook', '/new')} className="gap-2">
              <BookOpen size={18} />
              Add Station Log
            </Button>
            <Button variant="outline" onClick={() => handleQuickAction('askstewie', '/')} className="gap-2">
              <Robot size={18} />
              Ask Stewie
            </Button>
            {hasAnyRole(['ADMIN']) && (
              <Button variant="outline" onClick={() => navigate('/admin')} className="gap-2">
                <Users size={18} />
                Manage Users
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity Feed */}
      <div className="mt-6">
        <ActivityFeed 
          limit={8} 
          showHeader={true} 
          showFilters={true}
          compact={false}
          onViewMore={() => navigate('/activity')}
          appLinksConfig={appLinksConfig}
        />
      </div>
    </div>
  );
};

export default HubDashboard;
